import React, { useState, useRef } from "react";
import "./loan-calculator.css";

// Icons
import { FaClock, FaBalanceScale, FaChartPie, FaRupeeSign } from "react-icons/fa";

// Chart.js Essentials
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

ChartJS.register(ArcElement, Tooltip, Legend);

const LoanCalculator = () => {
    const [amount, setAmount] = useState(250000);
    const [days, setDays] = useState(90);
    const [rate, setRate] = useState(8);

    const mainRef = useRef(null);

    // Calculation
    const interest = (amount * rate * days) / (100 * 365);
    const total = amount + interest;

    const formatCurrency = (value) =>
        new Intl.NumberFormat("en-IN", {
            style: "currency",
            currency: "INR",
            minimumFractionDigits: 0,
        }).format(value);

    // ⭐ Enhanced 3D Pie Chart Data
    const pieData = {
        labels: ["Principal", "Interest"],
        datasets: [
            {
                data: [amount, interest],
                backgroundColor: [
                    "rgba(29,110,239,0.9)",
                    "rgba(233,78,78,0.9)",
                ],
                borderWidth: 3,
                borderColor: "#fff",
                hoverOffset: 15,
                cutout: "78%",
            },
        ],
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: "bottom",
                labels: {
                    color: "#333",
                    font: { size: 14, weight: "600" },
                    usePointStyle: true,
                },
            },
            tooltip: {
                backgroundColor: "#1d6eef",
                titleColor: "#fff",
                bodyColor: "#fff",
                padding: 12,
                callbacks: {
                    label: (ctx) => `${ctx.label}: ${formatCurrency(ctx.parsed)}`,
                },
            },
        },
        animation: {
            animateRotate: true,
            animateScale: true,
            duration: 1200,
            easing: "easeOutQuart",
        },
    };

    return (
        <div className="quantum-calculator-wrapper container " ref={mainRef}>
            <div className="text-center mb-5">

                <h2 className="display-4 calculator-title mb-2 text-dark">
                    Prism <span className="calculator-secondary-info">Flow Calculator</span>
                </h2>
            </div>

            <div className="orbit-container mx-auto">
                {/* Input Section */}
                <div className="input-core-panel p-4">
                    <h4 className="panel-title">Data Input Core</h4>

                    <div className="input-group-grid">
                        {[
                            {
                                id: "amount",
                                label: "Principal Amount (₹)",
                                value: amount,
                                min: 50000,
                                max: 5000000,
                                step: 10000,
                                icon: FaRupeeSign,
                                onChange: setAmount,
                                display: formatCurrency(amount),
                            },
                            {
                                id: "days",
                                label: "Loan Tenure (Days)",
                                value: days,
                                min: 30,
                                max: 730,
                                step: 10,
                                icon: FaClock,
                                onChange: setDays,
                                display: `${days} Days`,
                            },
                            {
                                id: "rate",
                                label: "Interest Rate (APR)",
                                value: rate,
                                min: 0.1,
                                max: 30,
                                step: 0.1,
                                icon: FaBalanceScale,
                                onChange: setRate,
                                display: `${rate}%`,
                            },
                        ].map((data) => (
                            <div className="orbit-bubble" key={data.id}>
                                <span className="bubble-label">
                                    <data.icon /> {data.label}
                                </span>

                                <span className="bubble-value">{data.display}</span>

                                <input
                                    type="range"
                                    min={data.min}
                                    max={data.max}
                                    step={data.step}
                                    value={data.value}
                                    onChange={(e) => data.onChange(+e.target.value)}
                                    className="form-range quantum-slider"
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Output Section */}
                <div className="output-orbit-panel">
                    <div className="results-grid">
                        {/* Chart */}
                        <div className="chart-field">
                            <h4 className="panel-title">
                                <FaChartPie className="me-2" /> Allocation Visualizer
                            </h4>

                            <div className="chart-core-wrapper">
                                <Doughnut data={pieData} options={pieOptions} />

                                <div className="chart-center-text">
                                    <span className="total-value-center">
                                        {formatCurrency(total).replace("₹", "")}
                                    </span>
                                    <small>Total Projection (₹)</small>
                                </div>
                            </div>
                        </div>

                        {/* Summary */}
                        <div className="summary-flow">
                            <h4 className="panel-title">Financial Summary</h4>

                            <div className="summary-box">
                                <div className="summary-item">
                                    <span>Principal Amount</span>
                                    <span className="fw-bold">
                                        {formatCurrency(amount)}
                                    </span>
                                </div>

                                <div className="summary-item">
                                    <span>Total Interest</span>
                                    <span className="fw-bold interest-value">
                                        {formatCurrency(interest)}
                                    </span>
                                </div>

                                <div className="total-payable-row mt-4">
                                    <span className="fw-bold">TOTAL PAYABLE</span>
                                    <span className="total-value-final">
                                        {formatCurrency(total)}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoanCalculator;
